/**
 * ReferencesCarousel component for displaying scrollable URL references
 * Features responsive design, smooth scrolling, and loading animations
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { Theme } from '../../theme/lightTheme';
import { throttle } from 'lodash';
import ReferenceItem from '../referenceItem';
import { ReferencesCarouselProps } from 'interfaces/components/referencesCarousel';

/**
 * Main container for the carousel
 * Adds padding for navigation arrows and handles overflow
 */
const CarouselContainer = styled.div<{ isRTL?: boolean }>`
  position: relative;
  width: 100%;
  margin: 1rem 0;
  padding: 0 2.5rem;
  overflow: hidden;
  direction: ${({ isRTL }) => (isRTL ? 'rtl' : 'ltr')};
`;

/**
 * Scrollable content container for reference items
 * Implements smooth scrolling with snap points
 */
const CarouselContent = styled.div<{ $isLoading: boolean }>`
  display: flex;
  gap: 1rem;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: 0.5rem;
  pointer-events: ${({ $isLoading }) => ($isLoading ? 'none' : 'auto')};

  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * Navigation arrow buttons for scrolling through references
 * Features translucent background with blur effect
 */
const ArrowButton = styled.button<{ theme: Theme; position: 'left' | 'right'; isRTL?: boolean }>`
  position: absolute;
  top: calc(50% - 20px);
  ${({ position, isRTL }) => {
    if (isRTL) {
      // In RTL mode, swap left and right positions
      return position === 'left' ? 'right: 0.5rem;' : 'left: 0.5rem;';
    }
    return position === 'left' ? 'left: 0.5rem;' : 'right: 0.5rem;';
  }}
  background: ${({ theme }) => theme.background + 'cc'};
  border: 1px solid ${({ theme }) => theme.text.light + '33'};
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 4;
  backdrop-filter: blur(4px);
  transition: opacity 0.2s ease;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 2rem;
    height: 2rem;
  }
`;

/**
 * Props interface for styled components that need visibility information
 */
interface ReferenceCardProps {
  /** Number of visible items in the carousel */
  visibleItems: number;
}

/**
 * Loading spinner with continuous rotation animation
 */
const Spinner = styled(motion.div).attrs({
  initial: { rotate: 0 },
  animate: { rotate: 360 },
  transition: { duration: 0.8, repeat: Infinity, ease: 'linear' },
})`
  width: 1rem;
  height: 1rem;
  border: 2px solid ${({ theme }) => theme.text.light + '88'};
  border-top-color: transparent;
  border-radius: 50%;
`;

/**
 * Placeholder card with gradient animation while loading
 * Matches size and shape of actual reference cards
 */
const SkeletonCard = styled(motion.div)<ReferenceCardProps>`
  flex: 0 0 calc((100% - ${({ visibleItems }) => visibleItems - 1}rem) / ${({ visibleItems }) => visibleItems});
  height: 120px;
  scroll-snap-align: start;
  border-radius: 12px;
  overflow: hidden;
  background: ${({ theme }) => `linear-gradient(90deg, ${theme.ai.color1}, ${theme.ai.color2}, ${theme.ai.color3})`};
  background-size: 200% 200%;
  opacity: 0.3;
  animation: gradientAnimation 1.8s ease infinite;

  @keyframes gradientAnimation {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;

/**
 * Shuffles array elements randomly
 * Used to randomize reference order
 *
 * @param array - Array to shuffle
 * @returns New shuffled array
 */
const shuffleArray = (array: string[]): string[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

/**
 * ReferencesCarousel component for displaying scrollable URL references
 * Features responsive design, loading states, and navigation controls
 *
 * @param references - Array of URLs to display as reference cards
 * @param simulateLoading - Whether to show loading animation (default: true)
 * @param isRTL - Whether to display in right-to-left mode
 * @returns Horizontal scrollable carousel of reference items
 */
const ReferencesCarousel: React.FC<ReferencesCarouselProps> = ({
  references,
  simulateLoading = true,
  isRTL = false,
}) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  // We'll determine the number of visible items based on the window width.
  const [visibleItems, setVisibleItems] = useState(3);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  const isScrolling = useRef(false);
  const [orderedReferences, setOrderedReferences] = useState<string[]>([]);
  const prevLoadedCount = useRef(0);
  const prevOrderedRefLength = useRef(0);
  const [list, setList] = useState<string[]>([]);
  // Track if loading is currently active
  const [isLoading, setIsLoading] = useState(simulateLoading);
  // Remember scroll position when loading stops
  const lastScrollPosition = useRef(0);
  // Flag to prevent reshuffling after loading is complete
  const hasStabilized = useRef(false);

  /**
   * Dynamically adjusts visible items based on screen width
   * Ensures optimal display across different device sizes
   */
  useEffect(() => {
    const updateVisibleItems = throttle(() => {
      const width = window.innerWidth;
      if (width < 1070) {
        setVisibleItems(1);
      } else if (width < 1600) {
        // Changed from 1480 to 1600 to ensure 3 items on 14" devices
        setVisibleItems(3);
      } else {
        setVisibleItems(5);
      }

      // Force recalculation of scroll position on resize
      if (carouselRef.current) {
        const gap = 16;
        const item = carouselRef.current.children[0] as HTMLElement;
        if (item) {
          const itemWidth = item.clientWidth + gap;
          const newIndex = Math.floor(carouselRef.current.scrollLeft / itemWidth);
          const newPosition = newIndex * itemWidth;

          // Only apply scroll position if significantly different to avoid jumps
          if (Math.abs(carouselRef.current.scrollLeft - newPosition) > 10) {
            carouselRef.current.scrollTo({ left: newPosition, behavior: 'auto' });
          }

          // Update current index to match new layout
          setCurrentIndex(newIndex);
        }
      }
    }, 100); // Throttle to 100ms for smoother resizing

    updateVisibleItems();
    window.addEventListener('resize', updateVisibleItems);
    return () => {
      window.removeEventListener('resize', updateVisibleItems);
      updateVisibleItems.cancel();
    };
  }, []);

  /**
   * Track previous state values for comparison
   */
  useEffect(() => {
    prevLoadedCount.current = loadedCount;
    prevOrderedRefLength.current = orderedReferences.length;
  });

  /**
   * Shuffles references for display order
   * Keeps first reference stable while shuffling the rest
   */
  useEffect(() => {
    // Skip reshuffling if loading has already completed
    if (hasStabilized.current && !isLoading) {
      return;
    }

    if (references.length <= 2) {
      setOrderedReferences(references);
    } else {
      const firstPatch = references.slice(0, 1);
      const secondPatch = references.slice(1);
      const shuffledSecond = shuffleArray(secondPatch);
      setOrderedReferences([...firstPatch, ...shuffledSecond]);
    }
  }, [references, isLoading]);

  /**
   * Scroll to a specific index in the carousel
   * Handles RTL direction by adjusting scroll direction
   */
  const scrollToIndex = useCallback(
    (index: number) => {
      if (carouselRef.current) {
        const container = carouselRef.current;
        const gap = 16; // Matches the gap in styled component
        const item = container.children[0] as HTMLElement;
        if (item) {
          const itemWidth = item.clientWidth + gap;
          // In RTL mode, we need to scroll in the opposite direction
          const scrollPosition = isRTL
            ? container.scrollWidth - container.clientWidth - index * itemWidth
            : index * itemWidth;

          container.scrollTo({
            left: scrollPosition,
            behavior: 'smooth',
          });

          setCurrentIndex(index);
        }
      }
    },
    [isRTL],
  );

  /**
   * Navigate to the next set of items
   */
  const scrollNext = useCallback(() => {
    if (isScrolling.current || !orderedReferences.length) return;

    const nextIndex = Math.min(currentIndex + visibleItems, Math.max(0, orderedReferences.length - visibleItems));

    scrollToIndex(nextIndex);
  }, [currentIndex, orderedReferences.length, visibleItems, scrollToIndex]);

  /**
   * Navigate to the previous set of items
   */
  const scrollPrev = useCallback(() => {
    if (isScrolling.current || !orderedReferences.length) return;

    const prevIndex = Math.max(0, currentIndex - visibleItems);

    scrollToIndex(prevIndex);
  }, [currentIndex, visibleItems, orderedReferences.length, scrollToIndex]);

  /**
   * Update final list when references or loading state changes
   */
  useEffect(() => {
    setList(orderedReferences);
  }, [orderedReferences]);

  /**
   * Handle references loading completion
   */
  useEffect(() => {
    if (loadedCount >= orderedReferences.length && orderedReferences.length > 0) {
      setTimeout(() => {
        setIsLoading(false);
        hasStabilized.current = true;

        // If we had a saved scroll position, restore it
        if (lastScrollPosition.current > 0 && carouselRef.current) {
          carouselRef.current.scrollLeft = lastScrollPosition.current;
        }
      }, 300);
    }
  }, [loadedCount, orderedReferences.length]);

  /**
   * Handle image load events
   */
  const handleImageLoad = () => {
    setLoadedCount((prev) => prev + 1);
  };

  /**
   * Handle loading state from parent component
   */
  useEffect(() => {
    // If parent says to simulate loading
    if (simulateLoading) {
      // Save current scroll position
      if (carouselRef.current && !isLoading) {
        lastScrollPosition.current = carouselRef.current.scrollLeft;
      }
      setIsLoading(true);
      setLoadedCount(0);
      hasStabilized.current = false;
    } else if (!simulateLoading && isLoading) {
      // Handle transition from loading to not loading
      setIsLoading(false);
      hasStabilized.current = true;

      // Restore scroll position
      setTimeout(() => {
        if (carouselRef.current && lastScrollPosition.current > 0) {
          carouselRef.current.scrollLeft = lastScrollPosition.current;
        }
      }, 100);
    }
  }, [simulateLoading]);

  // Render skeleton items while loading
  const renderSkeletons = () => {
    return Array(visibleItems)
      .fill(null)
      .map((_, index) => (
        <SkeletonCard
          key={`skeleton-${index}`}
          visibleItems={visibleItems}
          initial={{ opacity: 0.2 }}
          animate={{ opacity: 0.7 }}
          transition={{ repeat: Infinity, duration: 1.5, repeatType: 'reverse' }}
        />
      ));
  };

  return (
    <CarouselContainer isRTL={isRTL}>
      <AnimatePresence mode="wait">
        <CarouselContent ref={carouselRef} $isLoading={isLoading}>
          {isLoading
            ? renderSkeletons()
            : list.map((url, index) => (
                <ReferenceItem
                  key={`${url}-${index}`}
                  href={url}
                  visibleItems={visibleItems}
                  onLoad={handleImageLoad}
                />
              ))}
        </CarouselContent>
      </AnimatePresence>

      {/* Previous button - in RTL this is on the right side */}
      <ArrowButton
        position="left"
        isRTL={isRTL}
        onClick={scrollPrev}
        disabled={currentIndex === 0 || isLoading}
        aria-label="Previous references"
      >
        <FaChevronLeft size={14} />
      </ArrowButton>

      {/* Next button - in RTL this is on the left side */}
      <ArrowButton
        position="right"
        isRTL={isRTL}
        onClick={scrollNext}
        disabled={
          currentIndex >= orderedReferences.length - visibleItems ||
          orderedReferences.length <= visibleItems ||
          isLoading
        }
        aria-label="Next references"
      >
        <FaChevronRight size={14} />
      </ArrowButton>
    </CarouselContainer>
  );
};

export default ReferencesCarousel;
